// src/components/CryptoGate.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCrypto } from "../context/CryptoContext";

export default function CryptoGate({ onSignOut }) {
  const { t } = useTranslation();
  const { status, setup, completeSetup, unlock, recover } = useCrypto();

  const [mode, setMode] = useState("main"); // main | recovery
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [recInput, setRecInput] = useState("");
  const [shownRecoveryKey, setShownRecoveryKey] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = ["needs-setup", "locked", "error"].includes(status);
  if (!open) return null;

  const mapError = (e) => {
    switch (e?.message) {
      case "WEAK_PASSPHRASE":
        return t("Passphrase must be at least 12 characters");
      case "INVALID_RECOVERY_KEY":
        return t("Invalid recovery key format");
      case "KEYRING_EXISTS":
        return t("Encryption already set up. Reload the page.");
      default:
        return t("Wrong passphrase or recovery key");
    }
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      console.error("crypto gate error", e?.message);
      setError(mapError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetFields = () => {
    setPass("");
    setPass2("");
    setRecInput("");
  };

  const handleSetup = () => {
    if (pass !== pass2) return setError(t("Passphrases do not match"));
    run(async () => {
      const rk = await setup(pass);
      resetFields();
      setShownRecoveryKey(rk);
    });
  };

  const handleUnlock = () =>
    run(async () => {
      await unlock(pass);
      resetFields();
    });

  const handleRecover = () => {
    if (pass !== pass2) return setError(t("Passphrases do not match"));
    run(async () => {
      await recover(recInput, pass);
      resetFields();
      setMode("main");
    });
  };

  const handleDone = () => {
    completeSetup();
    setShownRecoveryKey(null);
    setSaved(false);
  };

  const downloadKey = () => {
    const blob = new Blob([shownRecoveryKey + "\n"], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "schedule-recovery-key.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const submitOnEnter = (fn) => (e) => {
    e.preventDefault();
    if (!busy) fn();
  };

  let title, body, actions;

  if (status === "error") {
    title = t("Something went wrong");
    body = (
      <Alert severity="error">
        {t("Could not load encryption data. Reload the page.")}
      </Alert>
    );
    actions = <Button onClick={onSignOut}>{t("Sign out")}</Button>;
  } else if (shownRecoveryKey) {
    title = t("Save your recovery key");
    body = (
      <>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            "This key is shown only once. If you forget your passphrase and lose this key, your schedules are lost forever. Nobody can recover them.",
          )}
        </Alert>
        <Box
          sx={{
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 1,
            fontFamily: "monospace",
            wordBreak: "break-all",
            userSelect: "all",
          }}
        >
          {shownRecoveryKey}
        </Box>
        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
          <Button
            size="small"
            onClick={() => navigator.clipboard.writeText(shownRecoveryKey)}
          >
            {t("Copy")}
          </Button>
          <Button size="small" onClick={downloadKey}>
            {t("Download")}
          </Button>
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={saved}
              onChange={(e) => setSaved(e.target.checked)}
            />
          }
          label={t("I saved my recovery key somewhere safe")}
        />
      </>
    );
    actions = (
      <Button variant="contained" disabled={!saved} onClick={handleDone}>
        {t("Continue")}
      </Button>
    );
  } else if (status === "needs-setup") {
    title = t("Protect your data");
    body = (
      <Box component="form" onSubmit={submitOnEnter(handleSetup)}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t(
            "Your schedules are encrypted in your browser. Choose a passphrase. We cannot reset it.",
          )}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          type="password"
          margin="dense"
          label={t("Passphrase (min 12 characters)")}
          value={pass}
          onChange={(e) => setPass(e.target.value)}
        />
        <TextField
          fullWidth
          type="password"
          margin="dense"
          label={t("Repeat passphrase")}
          value={pass2}
          onChange={(e) => setPass2(e.target.value)}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <button type="submit" hidden />
      </Box>
    );
    actions = (
      <>
        <Button onClick={onSignOut} disabled={busy}>
          {t("Sign out")}
        </Button>
        <Button variant="contained" onClick={handleSetup} disabled={busy}>
          {busy ? <CircularProgress size={20} /> : t("Create")}
        </Button>
      </>
    );
  } else if (mode === "recovery") {
    title = t("Reset with recovery key");
    body = (
      <Box component="form" onSubmit={submitOnEnter(handleRecover)}>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={t("Recovery key")}
          value={recInput}
          onChange={(e) => setRecInput(e.target.value)}
        />
        <TextField
          fullWidth
          type="password"
          margin="dense"
          label={t("New passphrase (min 12 characters)")}
          value={pass}
          onChange={(e) => setPass(e.target.value)}
        />
        <TextField
          fullWidth
          type="password"
          margin="dense"
          label={t("Repeat new passphrase")}
          value={pass2}
          onChange={(e) => setPass2(e.target.value)}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <button type="submit" hidden />
      </Box>
    );
    actions = (
      <>
        <Button
          onClick={() => {
            setMode("main");
            setError("");
            resetFields();
          }}
          disabled={busy}
        >
          {t("Back")}
        </Button>
        <Button variant="contained" onClick={handleRecover} disabled={busy}>
          {busy ? <CircularProgress size={20} /> : t("Reset")}
        </Button>
      </>
    );
  } else {
    title = t("Unlock your schedules");
    body = (
      <Box component="form" onSubmit={submitOnEnter(handleUnlock)}>
        <TextField
          autoFocus
          fullWidth
          type="password"
          margin="dense"
          label={t("Passphrase")}
          value={pass}
          onChange={(e) => setPass(e.target.value)}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => {
            setMode("recovery");
            setError("");
            resetFields();
          }}
        >
          {t("Forgot passphrase?")}
        </Button>
        <button type="submit" hidden />
      </Box>
    );
    actions = (
      <>
        <Button onClick={onSignOut} disabled={busy}>
          {t("Sign out")}
        </Button>
        <Button variant="contained" onClick={handleUnlock} disabled={busy}>
          {busy ? <CircularProgress size={20} /> : t("Unlock")}
        </Button>
      </>
    );
  }

  return (
    <Dialog open fullWidth maxWidth="xs" disableEscapeKeyDown>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}
